/**
 * Search Input Typing Animation Enhancement
 * Adds typewriter effect to search-page-input-component and predictive-search-component
 */

const SEARCH_COMPONENT_SELECTOR = 'search-page-input-component, predictive-search-component';

if (window.__searchInputTypingEnhancerInitialized) {
  // Prevent duplicate listeners/observers when this asset is loaded more than once.
} else {
  window.__searchInputTypingEnhancerInitialized = true;

  // Wait for components to be defined and enhance them with typing animation
  waitForComponents();
}

function waitForComponents() {
  const componentsToWait = ['search-page-input-component', 'predictive-search-component'];
  const definedComponents = componentsToWait.filter((name) => customElements.get(name));
  const undefinedComponents = componentsToWait.filter((name) => !customElements.get(name));

  if (definedComponents.length > 0 || undefinedComponents.length === 0) {
    enhanceSearchInputWithTyping();
  }

  // Wait for any undefined components
  undefinedComponents.forEach((componentName) => {
    customElements.whenDefined(componentName).then(() => {
      enhanceSearchInputWithTyping();
    });
  });
}

function enhanceSearchInputWithTyping() {
  if (window.__searchInputTypingEnhancerBound) return;
  window.__searchInputTypingEnhancerBound = true;

  // Add typing animation to all component instances
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initTypingForAllInputs();
      },
      { once: true }
    );
  } else {
    initTypingForAllInputs();
  }

  // Also init for any dynamically added components
  let scheduled = false;
  const pendingComponents = new Set();

  const scheduleInit = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      pendingComponents.forEach((component) => initTypingAnimation(component));
      pendingComponents.clear();
    });
  };

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          const element = /** @type {Element} */ (node);
          const tagName = element.tagName;
          if (tagName === 'SEARCH-PAGE-INPUT-COMPONENT' || tagName === 'PREDICTIVE-SEARCH-COMPONENT') {
            pendingComponents.add(element);
          } else {
            // Check for nested components
            if (element.querySelectorAll) {
              element.querySelectorAll(SEARCH_COMPONENT_SELECTOR).forEach((component) => {
                pendingComponents.add(component);
              });
            }
          }
        }
      });
    });

    scheduleInit();
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

function initTypingForAllInputs() {
  document.querySelectorAll(SEARCH_COMPONENT_SELECTOR).forEach(initTypingAnimation);
}

function initTypingAnimation(component) {
  // Avoid re-initialization
  if (component.dataset.typingInitialized) return;
  component.dataset.typingInitialized = 'true';

  // Find input within the component
  const input = component.querySelector('input[type="search"]');
  if (!input) return;

  // Get placeholder texts from data attributes
  const placeholder1 = input.getAttribute('data-placeholder-one');
  const placeholder2 = input.getAttribute('data-placeholder-two');
  const placeholder3 = input.getAttribute('data-placeholder-three');

  // Build placeholders array
  const placeholders = [placeholder1, placeholder2, placeholder3].filter((p) => p && p.trim());

  // Only animate if we have multiple placeholders
  if (placeholders.length <= 1) return;

  // Check mobile setting
  const isMobilePromptsEnabled = input.getAttribute('data-placeholder-prompts-mob') === 'true';
  const isMobile = window.innerWidth <= 749;

  if (isMobile && !isMobilePromptsEnabled) return;

  // Animation state
  let currentIndex = 0;
  let isAnimating = false;
  let currentPlaceholder = input.placeholder || '';

  // Get animation settings
  const typingSpeed = parseInt(input.getAttribute('data-typing-speed')) || 100;
  const deletingSpeed = parseInt(input.getAttribute('data-deleting-speed')) || 60;
  const delayAfterDeleting = parseInt(input.getAttribute('data-delay-after-deleting')) || 500;
  const delayBeforeFirstDelete = parseInt(input.getAttribute('data-delay-before-first-delete')) || 2000;
  const delayAfterWordTyped = parseInt(input.getAttribute('data-delay-after-word-typed')) || 2400;

  // Delay helper
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const setPlaceholder = (value) =>
    new Promise((resolve) => {
      requestAnimationFrame(() => {
        currentPlaceholder = value;
        input.placeholder = value;
        resolve();
      });
    });

  // Type placeholder text
  async function typePlaceholder(placeholder, speed) {
    // If new placeholder starts with current value, only type the suffix.
    let textToType = placeholder;
    if (currentPlaceholder.length >= 3 && placeholder.startsWith(currentPlaceholder)) {
      textToType = placeholder.slice(currentPlaceholder.length);
    }

    for (let charIndex = 0; charIndex < textToType.length; charIndex += 1) {
      if (!isAnimating) return;
      await delay(speed);
      if (!isAnimating) return;
      await setPlaceholder(currentPlaceholder + textToType.charAt(charIndex));
    }
  }

  // Delete placeholder text
  async function deletePlaceholder(nextPlaceholder, speed) {
    while (currentPlaceholder.length > 0) {
      if (!isAnimating) return;
      await delay(speed);
      if (!isAnimating) return;

      const nextValue = currentPlaceholder.slice(0, -1);
      await setPlaceholder(nextValue);

      // Stop early if we reached a useful common prefix.
      if (nextValue.length >= 3 && nextPlaceholder.startsWith(nextValue)) {
        return;
      }
    }
  }

  // Main animation loop
  async function animatePlaceholder() {
    // Wait before starting (only for first iteration)
    if (currentIndex === 0) {
      await delay(delayBeforeFirstDelete);
    }
    
    while (isAnimating) {
      const nextIndex = (currentIndex + 1) % placeholders.length;
      const nextPlaceholder = placeholders[nextIndex];
      
      // Delete current placeholder
      await deletePlaceholder(nextPlaceholder, deletingSpeed);
      if (!isAnimating) break;
      
      // Wait after deleting
      await delay(delayAfterDeleting);
      if (!isAnimating) break;
      
      // Type next placeholder
      await typePlaceholder(nextPlaceholder, typingSpeed);
      if (!isAnimating) break;
      
      // Update index
      currentIndex = nextIndex;
      
      // Wait after typing
      await delay(delayAfterWordTyped);
    }
  }

  // Start typing
  function startTyping() {
    if (isAnimating) return;
    isAnimating = true;
    animatePlaceholder();
  }

  function stopTyping() {
    isAnimating = false;
  }

  input.addEventListener('focus', stopTyping);

  input.addEventListener('blur', () => {
    if (!currentPlaceholder) {
      currentPlaceholder = placeholders[currentIndex] || '';
      input.placeholder = currentPlaceholder;
    }
    startTyping();
  });

  // Start the animation
  startTyping();
}
